import { faTimes, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, AppBar } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { Search, Toolbar, InputBase } from "./styles";
import { saveLinkEntity } from "./utils";
import { DocumentDTO, DocumentRef } from "../../models/documents";
import { TransactionRef } from "../../models/transactions";
import DialogWithConfirmationChangesDialog from "../DialogWithConfirmationChangesDialog/DialogWithConfirmationChangesDialog";
import LastLoadedEntities from "../LastLoadedEntities/LastLoadedEntities";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const LinkEntity = ({
  linkedEntity,
  entityToLinkName,
  linkedEntityName,
  handleNotifyParent,
  shouldClose,
  handleChangeDocumentToLink,
  legalEntitiesIds = [],
  withoutTransactions = false,
  transactionTypeId,
  customNameForDocToLink,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.link_entity"; // tRP - translation root path
  const translate = (expression: string): string => t(`${tRP}.${expression}`);

  const [state, setState] = useState<State>({
    query: "",
    selectedItem: null,
    loading: false,
    error: "",
    forceCloseMainDialog: false,
  });

  const [allowedDocType, setAllowedDocType] = useState<boolean>();

  const handleDiscardChanges = () =>
    setState((state) => ({
      ...state,
      query: "",
      selectedItem: null,
    }));

  const handleChangeQuery = (query: string): void => setState((state) => ({ ...state, query }));

  const handleSelectItem = (selectedItem: TransactionRef | DocumentRef): void =>
    setState((state) => ({ ...state, selectedItem }));

  const handleSaveLinkItem = async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await saveLinkEntity(linkedEntityName, linkedEntity, entityToLinkName, state.selectedItem);

      const { isSucceeded, isCanceled, error } = response;

      setState((state) => ({ ...state, loading: false, error }));

      if (isSucceeded || !isCanceled) {
        shouldClose && shouldClose();
        handleNotifyParent && handleNotifyParent();
      }
    } catch (error: any) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error.message ?? "",
      }));
    }
  };

  const handleConfirm = () => {
    handleChangeDocumentToLink && handleChangeDocumentToLink(state.selectedItem as DocumentDTO);
    setState((state) => ({
      ...state,
      forceCloseMainDialog: true,
    }));
  };

  const resetForceCloseMainDialog = () => {
    setState((state) => ({
      ...state,
      forceCloseMainDialog: false,
    }));
  };

  const disabledConfirmButton = useCallback(() => {
    return !(state.selectedItem && allowedDocType);
  }, [state.selectedItem, allowedDocType]);

  return (
    <>
      <DialogWithConfirmationChangesDialog
        openButton={<Button>{customNameForDocToLink ?? `${translate("link")} ${translate(entityToLinkName)}`}</Button>}
        title={
          customNameForDocToLink ??
          `${translate("link")} ${translate(entityToLinkName)}
            ${translate("to")} ${translate(linkedEntityName)}`
        }
        additionalActionButtonsInFooter={
          !handleChangeDocumentToLink ? (
            <Button
              variant="outlined"
              onClick={handleSaveLinkItem}
              disabled={!state.selectedItem}
            >
              {t("buttons.save")}
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleConfirm}
              disabled={disabledConfirmButton()}
            >
              {t("buttons.confirm")}
            </Button>
          )
        }
        hasChanges={!!state.selectedItem}
        handleDiscardChanges={handleDiscardChanges}
        forceCloseMainDialog={state.forceCloseMainDialog}
        resetForceCloseMainDialog={resetForceCloseMainDialog}
        maxWidth={"md"}
      >
        <LoadingOverlay loading={state.loading}>
          <AppBar
            position="static"
            color="transparent"
            elevation={0}
            sx={{ zIndex: 0 }}
          >
            <Toolbar disableGutters>
              <Search>
                <InputBase
                  placeholder="Search…" // TODO --- TRANSLATION
                  inputProps={{ "aria-label": "search" }}
                  value={state.query}
                  onChange={(e) => handleChangeQuery(e.target.value)}
                  endAdornment={
                    state.query ? (
                      <Button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleChangeQuery("")}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    ) : (
                      <FontAwesomeIcon icon={faSearch} />
                    )
                  }
                />
              </Search>
            </Toolbar>
          </AppBar>

          <LastLoadedEntities
            entities={`${entityToLinkName}s`} // TODO --- TRANSLATION
            query={state.query}
            handleSelect={(item) => handleSelectItem(item)}
            legalEntitiesIds={legalEntitiesIds}
            withoutTransactions={withoutTransactions}
            transactionTypeId={transactionTypeId}
            setAllowedDocType={(value: boolean) => setAllowedDocType(value)}
          />
        </LoadingOverlay>
      </DialogWithConfirmationChangesDialog>
    </>
  );
};

export default LinkEntity;
